import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { useMsal } from '@azure/msal-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://videoreel-api-habwcma9cygwhphm.westus-01.azurewebsites.net';
const PROFILE_API_KEY = 'd71894c289e14ff4a3edbb447e07fd49';

// ─── Data ────────────────────────────────────────────────────────────────────

const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],
  ['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
  ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
  ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],
  ['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],
  ['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
  ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
  ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
  ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
];

const EMPLOYMENT_STATUSES = [
  { value: 'employed_full', label: 'Employed full-time' },
  { value: 'employed_part', label: 'Employed part-time' },
  { value: 'self_employed', label: 'Self-employed' },
  { value: 'not_employed', label: 'Not currently employed' },
  { value: 'retired', label: 'Retired' },
  { value: 'student', label: 'Student' },
  { value: 'other', label: 'Other' },
];

const INCOME_RANGES = [
  { value: 'under_30k', label: 'Under $30,000' },
  { value: '30k_60k', label: '$30,000 – $60,000' },
  { value: '60k_100k', label: '$60,000 – $100,000' },
  { value: '100k_150k', label: '$100,000 – $150,000' },
  { value: 'over_150k', label: 'Over $150,000' },
  { value: 'prefer_not', label: 'Prefer not to say' },
];

const BIRTH_YEARS = Array.from({ length: 75 }, (_, i) => 2007 - i); // 2007 → 1933

// ─── Styled components ────────────────────────────────────────────────────────

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); }`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(6px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: ${fadeIn} 150ms ease;
`;

const Modal = styled.div`
  background: linear-gradient(160deg, #0f0f19 0%, #1a0a2e 100%);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 32px 28px 28px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.6);
  animation: ${slideUp} 200ms ease;
  color: white;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const Title = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
  color: white;
`;

const Subtitle = styled.p`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.45);
  margin: 0 0 28px;
  line-height: 1.5;
`;

const CloseButton = styled.button`
  background: rgba(255,255,255,0.07);
  border: none;
  color: rgba(255,255,255,0.6);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &:hover { background: rgba(255,255,255,0.14); color: white; }
`;

const FieldGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(255,255,255,0.55);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 8px;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 14px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 10px;
  color: white;
  font-size: 0.95rem;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='rgba(255,255,255,0.4)' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 36px;
  cursor: pointer;
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: rgba(255, 71, 87, 0.6);
  }

  option {
    background: #1a0a2e;
    color: white;
  }
`;

const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  gap: 8px;
`;

const Tile = styled.button`
  padding: 10px 8px;
  background: ${p => p.selected ? 'rgba(255, 71, 87, 0.25)' : 'rgba(255,255,255,0.05)'};
  border: 1px solid ${p => p.selected ? 'rgba(255, 71, 87, 0.7)' : 'rgba(255,255,255,0.1)'};
  border-radius: 10px;
  color: ${p => p.selected ? 'white' : 'rgba(255,255,255,0.65)'};
  font-size: 0.82rem;
  font-weight: ${p => p.selected ? '600' : '400'};
  cursor: pointer;
  transition: all 0.15s;
  text-align: center;
  line-height: 1.3;

  &:hover {
    background: rgba(255, 71, 87, 0.15);
    border-color: rgba(255, 71, 87, 0.4);
    color: white;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255,255,255,0.08);
  margin: 24px 0;
`;

const SaveButton = styled.button`
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #ff4757, #ff6b35);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.1s;
  margin-top: 4px;

  &:hover:not(:disabled) { opacity: 0.9; }
  &:active:not(:disabled) { transform: scale(0.98); }
  &:disabled { opacity: 0.45; cursor: default; }
`;

const StatusMsg = styled.div`
  text-align: center;
  font-size: 0.82rem;
  margin-top: 10px;
  color: ${p => p.error ? '#ff6b6b' : 'rgba(255,255,255,0.45)'};
`;

// ─── Component ────────────────────────────────────────────────────────────────

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'non_binary', label: 'Non-binary' },
  { value: 'prefer_not', label: 'Prefer not to say' },
];

const UserProfileModal = ({ onClose }) => {
  const { accounts } = useMsal();
  const [form, setForm] = useState({
    birth_year: '',
    gender: '',
    state: '',
    employment_status: '',
    income_range: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const userId = accounts[0]?.localAccountId || accounts[0]?.homeAccountId?.split('.')[0] || '';
  const profileHeaders = {
    'X-API-Key': PROFILE_API_KEY,
    'X-User-Id': userId,
  };

  // Load existing profile on mount
  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/profile`, { headers: profileHeaders });
        if (res.ok) {
          const data = await res.json();
          if (Object.keys(data).length > 0) {
            setForm({
              birth_year: data.birth_year?.toString() || '',
              gender: data.gender || '',
              state: data.state || '',
              employment_status: data.employment_status || '',
              income_range: data.income_range || '',
            });
          }
        }
      } catch (_) {
        // Non-fatal: just show empty form
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleSave = async () => {
    if (!userId) { setError('Not signed in.'); return; }
    setSaving(true);
    setStatus('');
    setError('');
    try {
      const body = {
        birth_year: form.birth_year ? parseInt(form.birth_year) : null,
        gender: form.gender || null,
        state: form.state || null,
        employment_status: form.employment_status || null,
        income_range: form.income_range || null,
      };
      const res = await fetch(`${API_URL}/api/profile`, {
        method: 'PUT',
        headers: { ...profileHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      setStatus('Profile saved!');
      setTimeout(onClose, 900);
    } catch (e) {
      setError('Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));


  return ReactDOM.createPortal(
    <Overlay onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <Modal onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <Title>My Profile</Title>
          <CloseButton onClick={onClose} aria-label="Close">✕</CloseButton>
        </ModalHeader>
        <Subtitle>
          Help advertisers show you more relevant content. All fields are optional.
        </Subtitle>

        {loading ? (
          <StatusMsg>Loading…</StatusMsg>
        ) : (
          <>
            <FieldGroup>
              <Label>Gender</Label>
              <TileGrid>
                {GENDER_OPTIONS.map(o => (
                  <Tile
                    key={o.value}
                    selected={form.gender === o.value}
                    onClick={() => set('gender', form.gender === o.value ? '' : o.value)}
                    type="button"
                  >
                    {o.label}
                  </Tile>
                ))}
              </TileGrid>
            </FieldGroup>

            <FieldGroup>
              <Label>Year of Birth</Label>
              <Select value={form.birth_year} onChange={e => set('birth_year', e.target.value)}>
                <option value="">Select year…</option>
                {BIRTH_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </Select>
            </FieldGroup>

            <FieldGroup>
              <Label>State</Label>
              <Select value={form.state} onChange={e => set('state', e.target.value)}>
                <option value="">Select state…</option>
                {US_STATES.map(([code, name]) => (
                  <option key={code} value={code}>{name}</option>
                ))}
              </Select>
            </FieldGroup>

            <FieldGroup>
              <Label>Employment Status</Label>
              <TileGrid style={{ gridTemplateColumns: '1fr 1fr' }}>
                {EMPLOYMENT_STATUSES.map(e => (
                  <Tile
                    key={e.value}
                    selected={form.employment_status === e.value}
                    onClick={() => setForm(f => ({ ...f, employment_status: f.employment_status === e.value ? '' : e.value }))}
                    type="button"
                    style={{ textAlign: 'left', fontSize: '0.78rem' }}
                  >
                    {e.label}
                  </Tile>
                ))}
              </TileGrid>
            </FieldGroup>

            <FieldGroup>
              <Label>Annual Household Income</Label>
              <TileGrid>
                {INCOME_RANGES.map(r => (
                  <Tile
                    key={r.value}
                    selected={form.income_range === r.value}
                    onClick={() => set('income_range', form.income_range === r.value ? '' : r.value)}
                    type="button"
                    style={{ fontSize: '0.78rem' }}
                  >
                    {r.label}
                  </Tile>
                ))}
              </TileGrid>
            </FieldGroup>

            <Divider />

            <SaveButton onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Profile'}
            </SaveButton>
            {status && <StatusMsg>{status}</StatusMsg>}
            {error && <StatusMsg error>{error}</StatusMsg>}
          </>
        )}
      </Modal>
    </Overlay>,
    document.body
  );
};

export default UserProfileModal;
